import { Info } from "lucide-react";
import { EvaluacionDropzone } from "@/features/evaluacion";
import type { EvaluacionContext } from "@/features/evaluacion";
import { ActividadStepper, type ActividadStep } from "./ActividadStepper";
import { getIntentosInfo } from "@/shared/utils/intentos";
import { IntentosBadge } from "@/shared/components/IntentosBadge";

interface ActividadUploadViewProps {
  actividad: {
    id: number;
    nombre: string;
    archivoLimpio?: { url?: string; llave?: string } | null;
    limiteIntentos?: number;
  };
  cerebro: string;
  aprendizId: number;
  actividadAprendiz:
    | {
        id?: number;
        intentos?: number;
        intentosExtras?: number;
        actividadPrograma?: {
          idPrograma?: number;
          programa?: { limiteIntentos?: number };
        };
      }
    | null
    | undefined;
  onFileUpload: (file: File) => void;
  isStreaming: boolean;
  isRetroReady: boolean;
  feedback: string | null;
  stepperError: boolean;
  isAnimating: boolean;
  onBack: () => void;
}

export function ActividadUploadView({
  actividad,
  cerebro,
  aprendizId,
  actividadAprendiz,
  onFileUpload,
  isStreaming,
  isRetroReady,
  feedback,
  stepperError,
  isAnimating,
  onBack,
}: ActividadUploadViewProps) {
  const currentStep: ActividadStep = stepperError
    ? "upload"
    : isRetroReady
      ? "feedback"
      : feedback && isStreaming
        ? "evaluating"
        : feedback
          ? "evaluating"
          : "upload";

  const intentosInfo = getIntentosInfo({
    intentos: actividadAprendiz?.intentos,
    intentosExtras: actividadAprendiz?.intentosExtras,
    limiteIntentos: actividadAprendiz?.actividadPrograma?.programa?.limiteIntentos,
  });

  const context: EvaluacionContext = {
    actividadId: actividad.id,
    aprendizId,
    actividadAprendizId: actividadAprendiz?.id,
    cerebro,
    archivoLimpioUrl: actividad.archivoLimpio?.url || "",
    archivoLimpioKey: actividad.archivoLimpio?.llave || "",
    programa: actividadAprendiz?.actividadPrograma?.idPrograma ?? 0,
    limiteIntentos: intentosInfo.total,
  };

  return (
    <>
      {/* Botón volver atrás */}
      <div className="flex items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 bg-transparent border-none text-[var(--primary)] font-medium text-base hover:font-bold cursor-pointer transition-all p-0 shadow-none"
          style={{ minWidth: 0 }}
        >
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Atrás
        </button>
      </div>

      {/* Título y badge de intentos */}
      <div className="flex flex-col items-center mb-2 md:mb-3">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--primary)] mb-1 text-center">
          Sube tu evidencia o reto
        </h1>
        {actividadAprendiz && (
          <IntentosBadge
            info={intentosInfo}
            icon={<Info size={14} />}
            className="flex items-center gap-1 bg-white dark:bg-[var(--card-background)] rounded-full shadow-sm px-3 py-1 border border-gray-100 dark:border-[var(--border)] w-fit text-xs text-[var(--primary)] font-medium"
          />
        )}
      </div>

      <div
        className={`p-6 md:p-8 flex flex-col pb-8 ${
          isAnimating
            ? "transition-all duration-300 ease-in-out opacity-0 -translate-x-8"
            : "opacity-100 translate-x-0"
        }`}
      >
        <div className="relative overflow-visible mb-4 md:mb-6 z-20">
          <EvaluacionDropzone
            context={context}
            intentosUsados={intentosInfo.usados}
            onFileSelected={onFileUpload}
            colorPrimario="var(--primary)"
          />
        </div>

        <ActividadStepper currentStep={currentStep} hasError={stepperError} />
      </div>
    </>
  );
}
